import { useState, useEffect, useRef, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { useGainMoleDashExpMutation } from 'vvs-gotchi-graphql'
import useToast from 'hooks/useToast'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { useTranslation } from 'contexts/Localization'
import { toastTypes } from 'components/Toast'
import useGotchiOwner from 'state/vvsgotchi/hooks/useGotchiOwner'
import { moleDashInfoFromOwner } from 'state/vvsgotchi/quests/moleDash'
import PixelatedImage from 'views/VvsGotchi/components/PixelatedImage'
import QuestDetailTabs from 'views/VvsGotchi/components/QuestDetailTabs'
import { questSymbolToImageUrl, questCategoryDetails } from 'views/VvsGotchi/config'
import { CloseButton } from 'components/CloseButton'
import { loadBgm } from 'state/vvsgotchi/bgm'
import { useVvsGotchiAudioModeManager, useBgm, MUTED } from 'state/vvsgotchi/hooks/useConfigManager'
import moleDashBgmSrc from 'assets/vvsgotchi/moleDashGame/bgm.mp3'
import historyBackOrReplace from 'utils/historyBackOrReplace'
import { Path } from 'config/constants/paths'
import * as ga from 'utils/googleAnalyticsHelper'

import { QuestDetailPageProps } from 'views/VvsGotchi/QuestDetail/types'

import { MOLE_DASH_MAX_EXP } from './config'

import { InitGameInfoFlex, ResultGameInfoFlex } from './components/GameInfoSections'
import GameOverTexts from './components/GameOverTexts'
import {
  PageContainer,
  MoleDashCanvasContainer,
  CanvasCoverBox,
  CloseButtonBox,
  TitleBox,
  GameEntryFlex,
  GameInfoActionButton,
  QuestDetailLayer,
} from './components/styles'
import { MoleDashGameResult } from './types'

const GAME_OVER_POPUP_WAIT = 1500 // ms

let MoleDash: any

const MoleDashQuestDetailPage: React.FC<QuestDetailPageProps> = ({ quest }) => {
  const { t } = useTranslation()
  const history = useHistory()
  const { toastVVSGotchi } = useToast()
  const { account } = useActiveWeb3React()
  const { isOnboarded, owner, refresh } = useGotchiOwner()
  const [gainMoleDashExpMutate] = useGainMoleDashExpMutation()
  const { sePlay } = useVvsGotchiAudioModeManager()

  const [gameLoaded, setGameLoaded] = useState(false)
  const [focusOnGame, setFocusOnGame] = useState(false)
  const [playing, setPlaying] = useState(false)
  const [gameOver, setGameOver] = useState(false)
  const [gameResult, setGameResult] = useState<MoleDashGameResult>()
  const gameCanvasContainerRef = useRef<HTMLDivElement>()
  const pageContainer = useRef<HTMLDivElement>()
  const moleDashGame = useRef<any>(null)
  const sePlayRef = useRef(sePlay)
  sePlayRef.current = sePlay
  const claimMoleDashExpRef = useRef<(lasted: number) => Promise<MoleDashGameResult>>()

  useBgm(playing ? moleDashBgmSrc : focusOnGame ? MUTED : null)

  const moleDashInfo = moleDashInfoFromOwner(owner)

  const claimMoleDashExp = useCallback(
    async (newLasted: number) => {
      try {
        const { data, errors } = await gainMoleDashExpMutate({
          variables: {
            address: account,
            lasted: newLasted,
          },
        })

        if (errors) throw errors

        await refresh()

        return {
          lasted: newLasted,
          gainedExp: data.gainMoleDashExp.gainedExp,
          newRecord: Math.floor(newLasted / 1000) > Math.floor(moleDashInfo.bestRecord.lasted / 1000),
        }
      } catch (error) {
        console.error(error)
        toastVVSGotchi(
          t('Failed to claim EXP'),
          toastTypes.INFO,
          <PixelatedImage src="/images/vvsGotchi/cross.png" width="18px" />,
          t('Something went wrong'),
        )

        return {
          lasted: newLasted,
          gainedExp: 0,
          newRecord: false,
        }
      }
    },
    [account, gainMoleDashExpMutate, refresh, moleDashInfo, toastVVSGotchi, t],
  )
  claimMoleDashExpRef.current = claimMoleDashExp

  useEffect(() => {
    let gameCanvasContainer: HTMLDivElement
    let umounted = false

    const loadAndinitDashGame = async () => {
      if (!MoleDash) {
        MoleDash = (await import('./MoleDash/MoleDash')).default
      }
      if (!moleDashGame.current && gameCanvasContainerRef.current) {
        await Promise.all([MoleDash.loadResources(), loadBgm(moleDashBgmSrc)])

        gameCanvasContainer = gameCanvasContainerRef.current
        const game = new MoleDash(gameCanvasContainer)
        game.onGameStart = () => {
          if (umounted) return

          setPlaying(true)
          setFocusOnGame(true)
          setGameOver(false)

          ga.gotchiMoleDashStartGame()
        }
        game.onGameOver = async () => {
          if (umounted) return

          const newLasted = Math.floor(game.runningTime)
          setPlaying(false)
          setGameResult(null)
          setGameOver(true)

          ga.gotchiMoleDashGameOver(newLasted)

          const [result] = await Promise.all([
            claimMoleDashExpRef.current(newLasted),
            new Promise((r) => setTimeout(r, GAME_OVER_POPUP_WAIT)),
          ])
          if (umounted) return

          game.readyToRestart = true
          setGameResult(result)
          setFocusOnGame(false)
        }
        game.muted = !sePlayRef.current

        moleDashGame.current = game
        setGameLoaded(true)
      }
    }

    if (isOnboarded) {
      loadAndinitDashGame()
    }

    return () => {
      umounted = true
      if (moleDashGame.current) {
        moleDashGame.current.stop()
        moleDashGame.current.stopListening()
        moleDashGame.current = null
        setGameLoaded(false)
      }
      if (gameCanvasContainer) {
        gameCanvasContainer.innerHTML = ''
      }
    }
  }, [isOnboarded])

  useEffect(() => {
    if (moleDashGame.current) {
      moleDashGame.current.muted = !sePlay
    }
  }, [sePlay])

  useEffect(() => {
    if (focusOnGame) {
      pageContainer.current.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [focusOnGame])

  return (
    <PageContainer ref={pageContainer} focusOnGame={focusOnGame}>
      <MoleDashCanvasContainer ref={gameCanvasContainerRef} />

      <CanvasCoverBox
        bg={questCategoryDetails[quest?.category]?.bgImageUrl}
        grayout={!isOnboarded}
        closed={gameLoaded}
        blockPointerEvents={!focusOnGame}
      />

      <CloseButtonBox>
        <CloseButton fill="black" onClick={() => historyBackOrReplace(history, Path.VVS_GOTCHI_QUEST)} />
      </CloseButtonBox>

      <TitleBox focusOnGame={focusOnGame} hidden={gameOver} grayout={!isOnboarded}>
        <PixelatedImage src={questSymbolToImageUrl[quest.symbol]} />
      </TitleBox>

      {gameOver && <GameOverTexts gameResult={gameResult} />}

      <GameEntryFlex focusOnGame={focusOnGame}>
        {!gameOver ? (
          <InitGameInfoFlex moleDashInfo={moleDashInfo} quest={quest} />
        ) : (
          <ResultGameInfoFlex moleDashInfo={moleDashInfo} gameResult={gameResult} />
        )}

        <GameInfoActionButton
          disabled={!isOnboarded || !gameLoaded}
          onClick={() => {
            if (moleDashGame.current && gameOver) {
              moleDashGame.current.restart()
            } else {
              setFocusOnGame(true)
            }
          }}
        >
          {!isOnboarded
            ? t('Get Onboarded First!')
            : !gameLoaded
            ? t('Loading...')
            : moleDashInfo.todayExp >= MOLE_DASH_MAX_EXP
            ? t('Practice (No EXP)')
            : gameOver
            ? t('Start Again!')
            : t('Let’s Start!')}
        </GameInfoActionButton>
      </GameEntryFlex>

      <QuestDetailLayer focusOnGame={focusOnGame}>
        <QuestDetailTabs owner={owner} quest={quest} />
      </QuestDetailLayer>
    </PageContainer>
  )
}

export default MoleDashQuestDetailPage
